import { gql } from '@apollo/client';
import { useMutation } from '@apollo/client/react';
import { useState } from 'react';
import { toast } from 'sonner';
import { z } from 'zod';

import { EditCheckboxField } from '../../../EditCheckboxField';
import { EditDialog } from '../../../EditDialog';
import { EditFormField } from '../../../EditFormField';
import type { ContactOtherFieldsData } from '../queries/contactOthersQuery';
import { GET_CONTACT_OTHERS } from '../queries/contactOthersQuery';

const UPDATE_CONTACT = gql`
  mutation UpdateContact($id: ID!, $values: ContactInput!) {
    updateContact(id: $id, values: $values) {
      ok
      contact {
        id
        doNotSendMonthlyUpdates
        newZapRsvp
        crmUrl
      }
    }
  }
`;

const COMMUNICATION_FIELDS: Record<
  string,
  { apiField: string; isBoolean?: boolean }
> = {
  crmUrl: { apiField: 'crmUrl' },
  doNotSendMonthlyUpdates: {
    apiField: 'doNotSendMonthlyUpdates',
    isBoolean: true
  },
  newZapRsvp: { apiField: 'newZapRsvp' }
};

const urlSchema = z.string().refine(
  val => {
    if (val === '') return true;
    return z.url().safeParse(val).success;
  },
  {
    message: 'Please enter a valid URL (e.g., https://example.com)'
  }
);

export interface EditCommunicationDialogProps {
  contactId: string;
  initialValue: ContactOtherFieldsData;
  isOpen: boolean;
  onChange: (data: ContactOtherFieldsData) => void;
  onOpenChange: (open: boolean) => void;
  onSave: () => void;
  value: ContactOtherFieldsData;
}

export function EditCommunicationDialog({
  contactId,
  initialValue,
  isOpen,
  onChange,
  onOpenChange,
  onSave,
  value
}: EditCommunicationDialogProps) {
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSaving, setIsSaving] = useState(false);

  const [updateContact] = useMutation(UPDATE_CONTACT, {
    refetchQueries: [{ query: GET_CONTACT_OTHERS, variables: { contactId } }]
  });

  const changes = getChangedValues(value, initialValue);
  const isDirty = Object.keys(changes).length > 0;

  const validateField = (field: string, fieldValue: string) => {
    if (field === 'crmUrl') {
      const result = urlSchema.safeParse(fieldValue);
      if (!result.success && result.error.issues.length > 0) {
        setErrors(prev => ({
          ...prev,
          [field]: result.error.issues[0]?.message ?? 'Invalid URL'
        }));
      } else {
        setErrors(prev => {
          const newErrors = { ...prev };
          // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
          delete newErrors[field];
          return newErrors;
        });
      }
    }
  };

  const updateField = <K extends keyof ContactOtherFieldsData>(
    field: K,
    fieldValue: ContactOtherFieldsData[K]
  ) => {
    if (field === 'crmUrl') {
      validateField(field, fieldValue as string);
    }
    onChange({ ...value, [field]: fieldValue });
  };

  const handleSave = async () => {
    // Validate CRM URL before saving
    const result = urlSchema.safeParse(value.crmUrl);
    if (!result.success) {
      setErrors({
        crmUrl: result.error.issues[0]?.message ?? 'Invalid URL'
      });
      return;
    }

    if (!isDirty) {
      onSave();
      return;
    }

    setIsSaving(true);

    try {
      const response = await updateContact({
        variables: { id: contactId, values: changes }
      });

      const data = response.data as {
        updateContact?: { ok?: boolean };
      };

      if (data.updateContact?.ok === false) {
        toast.error('Failed to save Communication');
      } else {
        setErrors({});
        toast.success('Communication saved successfully');
        onSave();
      }
    } catch (error) {
      console.error('Error saving Communication:', error);
      toast.error('Failed to save Communication');
    } finally {
      setIsSaving(false);
    }
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      setErrors({});
    }
    onOpenChange(open);
  };

  return (
    <EditDialog
      isDirty={isDirty}
      isOpen={isOpen}
      isSaving={isSaving}
      onOpenChange={handleOpenChange}
      onSave={handleSave}
      title="Edit Communication"
    >
      <div className="grid grid-cols-2 gap-6">
        <EditCheckboxField
          checked={value.doNotSendMonthlyUpdates}
          label="Do not send Monthly Updates"
          onCheckedChange={v => {
            updateField('doNotSendMonthlyUpdates', v);
          }}
        />
        <EditFormField
          label="New Zap RSVP"
          onChange={v => {
            updateField('newZapRsvp', v);
          }}
          value={value.newZapRsvp}
        />
        <EditFormField
          error={errors.crmUrl}
          label="CRM URL"
          onChange={v => {
            updateField('crmUrl', v);
          }}
          placeholder="Enter CRM URL"
          type="url"
          value={value.crmUrl}
        />
      </div>
    </EditDialog>
  );
}

function getChangedValues(
  current: ContactOtherFieldsData,
  initial: ContactOtherFieldsData
): Record<string, boolean | null | string> {
  const changes: Record<string, boolean | null | string> = {};

  for (const [uiField, config] of Object.entries(COMMUNICATION_FIELDS)) {
    const currentVal = current[uiField as keyof ContactOtherFieldsData];
    const initialVal = initial[uiField as keyof ContactOtherFieldsData];

    if (currentVal !== initialVal) {
      if (config.isBoolean) {
        changes[config.apiField] = currentVal as boolean;
      } else {
        const strVal = currentVal as null | string;
        changes[config.apiField] = strVal === '' ? null : (strVal ?? null);
      }
    }
  }

  return changes;
}
